// hooks/useSession.ts — reactive session/auth hook.
//
// Reads sessionStore reactively via Zustand and delegates mutations to authService.
// This is the ONLY surface components should use for auth state and actions.

import { useSessionStore } from '@/core/stores/sessionStore';
import { authService } from '@/core/services/authService';
import type { User, ProfileOverrides } from '@/core/models';
import type { SignUpFields, SignUpResult } from '@/core/services/authService';

export interface SessionHook {
  /** The active user's merged registry record, or null when signed out. */
  currentUser: User | null;
  /** True when there is a signed-in active account. */
  isAuthenticated: boolean;
  /** Sign in by username/handle/email + password. Throws InvalidCredentials on failure. */
  signIn: (usernameOrHandle: string, password: string) => Promise<string>;
  /** Completes a 2FA sign-in challenge (see authService.TotpRequired). */
  completeTotpSignIn: (challenge: string, code: string) => Promise<string>;
  /** Create a new account. Does NOT auto-login. Resolves with the id and the
   *  invite-code verdict (1.24.00: 'applied' | 'invalid' | null). */
  signUp: (fields: SignUpFields) => Promise<SignUpResult>;
  /** Sign out the current user. */
  signOut: () => void;
  /** Persist profile edits via the API and re-cache the user. */
  updateProfile: (fields: ProfileOverrides) => Promise<boolean>;
  /** Change the active account's password. */
  updatePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  /** Delete the active account. */
  deleteAccount: (password: string) => Promise<void>;
  /** Download the active account's data as JSON (sans password). No-op if signed out. */
  downloadData: () => Promise<void>;
  /** Ends every other session on the account; resolves to how many were ended. */
  signOutOtherSessions: () => Promise<number>;
  /** Hydrate account profile overlays onto the users registry (call at app boot). */
  hydrateAccountsIntoUsersRegistry: () => void;
  /** Whether the active account is premium (reactive — a session mirror; see
   * hooks/useBilling.ts for the authoritative real-time tier/status). */
  isPremium: boolean;
  /** Whether the active account is platform staff (reactive). */
  isAdmin: boolean;
  /** Whether the active account is a superuser (reactive). */
  isSuperuser: boolean;
  /** Whether the active account's email is verified (reactive). */
  isEmailVerified: boolean;
  /** Confirm an email via the verification-link token. */
  verifyEmail: (token: string) => Promise<void>;
  /** Re-send the verification email (signed-in only). */
  resendVerification: () => Promise<void>;
}

export function useSession(): SessionHook {
  // Subscribe to the accounts array so the hook re-renders on every change.
  useSessionStore((s) => s.accounts);

  return {
    get currentUser() {
      return authService.getCurrentUser();
    },
    get isAuthenticated() {
      return authService.isAuthenticated();
    },
    signIn: (u, p) => authService.signIn(u, p),
    completeTotpSignIn: (challenge, code) => authService.completeTotpSignIn(challenge, code),
    signUp: (f) => authService.signUp(f),
    signOut: () => authService.signOut(),
    updateProfile: (f) => authService.updateProfile(f),
    updatePassword: (cur, next) => authService.updatePassword(cur, next),
    deleteAccount: (password) => authService.deleteAccount(password),
    downloadData: () => authService.downloadData(),
    signOutOtherSessions: () => authService.signOutOtherSessions(),
    hydrateAccountsIntoUsersRegistry: () =>
      authService.hydrateAccountsIntoUsersRegistry(),
    get isPremium() {
      return authService.isPremium();
    },
    get isAdmin() {
      return authService.isAdmin();
    },
    get isSuperuser() {
      return authService.isSuperuser();
    },
    get isEmailVerified() {
      return authService.getCurrentUser()?.emailVerified ?? false;
    },
    verifyEmail: (token) => authService.verifyEmail(token),
    resendVerification: () => authService.resendVerification(),
  };
}
